import React, { createContext, useContext, useState, useEffect } from "react";
import { Modal } from "antd";
import * as authService from "../services/auth/authService";
import { getCookie, setCookie } from "../utils/cookie";
import { config } from "../config";
import { useTranslation } from "react-i18next";
import sseService from "../services/sseService";

const AuthContext = createContext(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const initAuth = async () => {
      try {
        const savedUser = localStorage.getItem("user");
        const savedAuth = localStorage.getItem("isAuthenticated");
  
        if (savedUser && savedAuth === "true") {
          // 非 mock 模式下，校验后端 session 是否仍然有效
          // 防止 Cookie 已过期但 localStorage 残留导致的循环跳转
          if (!config.mock) {
            try {
              const userInfoRes = await authService.getUserInfo();
              if (!userInfoRes.success) {
                console.warn("后端 session 已过期，清除本地认证数据");
                clearAuthData();
                return;
              }
            } catch {
              console.warn("校验后端 session 失败，清除本地认证数据");
              clearAuthData();
              return;
            }
          }

          const userData = JSON.parse(savedUser);
          setUser(userData);
          setIsAuthenticated(true);
        } else {
          // 在mock模式下，如果localStorage中有基本的用户标识，也认为已登录
          const mockUser = localStorage.getItem('mockUser');
          if (mockUser) {
            const userData = {
              loginId: 'SYS',
              username: '系统',
              name: '系统',
              avatar: "",
              email: "",
              mobile: "",
              orgName: "",
            };
            setUser(userData);
            setIsAuthenticated(true);
            localStorage.setItem("user", JSON.stringify(userData));
            localStorage.setItem("isAuthenticated", "true");
          }
        }
      } catch (error) {
        console.error("初始化认证状态失败:", error);
        clearAuthData();
      } finally {
        setLoading(false);
      }
    };
  
    initAuth();
  }, []);

  const clearAuthData = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("token");
    localStorage.removeItem("mockUser"); //✅ 清除mock用户标识
    // ✅ 清除 Cookie
    setCookie("Cookie", "", -1); // 设置过期时间为负数来删除 Cookie
  };

  const login = async (username, password) => {
    try {
      setLoading(true);
      const response = await authService.login({ username, password });

      if (response.success) {
        const loginData = response.data;

        const { requireChangePasswordType, changePasswordReason } = loginData;
        const hasForceChangePassword = requireChangePasswordType === "force";
        // const hasChangePassword =
        //   requireChangePasswordType === "force" ||
        //   requireChangePasswordType === "toast";

        // 需要强制修改密码
        if (hasForceChangePassword) {
          Modal.warning({
            title: t("login.passwordExpired"),
            content: t("login.passwordExpiredContent"),
          });
          setTimeout(() => {
            location.href = `/login/reset-pwd?loginId=${encodeURIComponent(loginData.loginId || username)}`;
          }, 2000);
          return false;
        }

        const userData = {
          loginId: loginData.loginId,
          username: loginData.name,
          name: loginData.name,
          avatar: "",
          email: "",
          mobile: "",
          orgName: "",
        };

        setUser(userData);
        setIsAuthenticated(true);
        
        localStorage.setItem("user", JSON.stringify(userData));
        localStorage.setItem("isAuthenticated", "true");
        localStorage.setItem("token", loginData.token); //✅ 保存 token到 localStorage
        localStorage.setItem("mockUser", "true"); //✅ 添加mock用户标识
                
        //✅ 设置 Cookie (支持 mock模式)
        setCookie("Cookie", loginData.token);

        // 登录成功后建立 SSE 连接，接收实时通知推送
        sseService.connect();

        return true;
      }
      return { success: false, errorCode: response.errorCode, errorMessage: response.errorMessage };
    } catch (error) {
      console.error("登录失败:", error);
      return { success: false, errorCode: null, errorMessage: null };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      // 登出时断开 SSE 连接，同时通知后端清理
      sseService.disconnect(true);
      // 先通过 GET 请求调用登出接口，再重定向
      await fetch("/auth/api/v1/account/do-logout", { method: "GET" });
      location.href = "/auth/api/v1/account/do-logout";
    } catch (error) {
      console.error("登出失败:", error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      clearAuthData();
    }
  };

  /**
   * SSO 登录 - 获取后端 SSO 配置，然后跳转到 MS Entra authorize URL。
   * 如果是 mock 模式则走原有的 mock 逻辑。
   */
  const ssoLogin = async () => {
    try {
      setLoading(true);

      // 先获取 SSO 配置
      const configResponse = await authService.getSSOConfig();
      if (!configResponse.success || !configResponse.data?.enabled) {
        console.warn("SSO not enabled or config fetch failed");
        return false;
      }

      const { authorizeUrl, clientId, scope } = configResponse.data;
      const redirectUri = encodeURIComponent(window.location.origin + '/login');
      const encodedScope = encodeURIComponent(scope || 'openid profile email');

      // 跳转到 MS Entra authorize endpoint
      // prompt=login 强制 MS Entra 每次登录都要求重新认证，不复用已有会话
      window.location.href = `${authorizeUrl}?client_id=${clientId}&response_type=code&redirect_uri=${redirectUri}&scope=${encodedScope}&prompt=login`;

      // 浏览器即将跳走，返回一个永不 resolve 的 Promise，
      // 防止调用方误判为失败而弹出错误提示
      return new Promise(() => {});
    } catch (error) {
      console.error("SSO登录失败:", error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * SSO 回调处理 - 用 authorization code 换取 session
   */
  const ssoCallback = async (code) => {
    try {
      setLoading(true);
      const response = await authService.handleSSOCallback(code, 'PC');

      if (response.success) {
        const loginData = response.data;

        const userData = {
          loginId: loginData.loginId,
          username: loginData.name,
          name: loginData.name,
          avatar: "",
          email: "",
          mobile: "",
          orgName: "",
        };

        setUser(userData);
        setIsAuthenticated(true);

        localStorage.setItem("user", JSON.stringify(userData));
        localStorage.setItem("isAuthenticated", "true");
        localStorage.setItem("token", loginData.token);
        localStorage.setItem("mockUser", "true");

        setCookie("Cookie", loginData.token);

        // SSO 登录成功后建立 SSE 连接，接收实时通知推送
        sseService.connect();

        return true;
      }
      return false;
    } catch (error) {
      console.error("SSO回调处理失败:", error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    login,
    logout,
    ssoLogin,
    ssoCallback,
    setIsAuthenticated,
    clearAuthData,
    isAuthenticated,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
